import { CheckCircle2, Circle, Clock, ListTodo, XCircle } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';

import { BaseBadge } from './BaseBadge';
import { BaseToolBadgeProps } from './types';

const TaskSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  status: z.string(),
  canonical_status: z.string(),
  type: z.string(),
  canonical_type: z.string(),
  group: z.string(),
  canonical_group: z.string(),
  due_date: z.string().nullable(),
  assignee: z.string().nullable(),
});

const ListTasksResponseSchema = z.array(TaskSchema);

const getStatusIcon = (canonicalStatus: string) => {
  switch (canonicalStatus) {
    case 'Completed':
      return <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />;
    case 'In Progress':
      return <Clock className="h-3.5 w-3.5 text-blue-600" />;
    case 'Blocked':
      return <XCircle className="h-3.5 w-3.5 text-red-600" />;
    case 'Pending':
      return <Circle className="h-3.5 w-3.5 text-gray-400" />;
    default:
      return <Circle className="h-3.5 w-3.5 text-gray-400" />;
  }
};

const getStatusColor = (canonicalStatus: string) => {
  switch (canonicalStatus) {
    case 'Completed':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'In Progress':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'Blocked':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'Pending':
      return 'bg-gray-50 text-gray-700 border-gray-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

type TaskItemProps = {
  task: z.infer<typeof TaskSchema>;
};

const TaskItem = ({ task }: TaskItemProps) => {
  return (
    <div className="flex flex-col gap-2 rounded-md p-3 hover:bg-muted/30">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {getStatusIcon(task.canonical_status)}
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <span className="text-xs font-medium text-foreground truncate">
              {task.name}
            </span>
            {task.description && (
              <span className="text-xs text-muted-foreground line-clamp-2">
                {task.description}
              </span>
            )}
          </div>
        </div>
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] px-2 py-0.5',
            getStatusColor(task.canonical_status),
          )}
        >
          {task.status}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-2 pl-6">
        <Badge variant="secondary" className="text-[10px] px-2 py-0.5">
          {task.group}
        </Badge>
        <Badge variant="secondary" className="text-[10px] px-2 py-0.5">
          {task.type}
        </Badge>
        {task.assignee && (
          <Badge variant="outline" className="text-[10px] px-2 py-0.5">
            {task.assignee}
          </Badge>
        )}
      </div>
    </div>
  );
};

type TaskListProps = {
  tasks: z.infer<typeof TaskSchema>[];
};

const TaskList = ({ tasks }: TaskListProps) => {
  return (
    <div className="flex flex-col divide-y divide-border">
      {tasks.map((task) => (
        <TaskItem key={task.id} task={task} />
      ))}
    </div>
  );
};

export const ListTasksBadge = ({ isDone, ...props }: BaseToolBadgeProps) => {
  const allProps = { ...props, isDone };

  const tasks = useMemo(() => {
    if (!props.toolMessage) return [];
    try {
      if (typeof props.toolMessage.content !== 'string') {
        return [];
      }
      const parsed = JSON.parse(props.toolMessage.content);
      const validated = ListTasksResponseSchema.safeParse(parsed);

      if (validated.success) {
        return validated.data;
      }
      return [];
    } catch (error) {
      return [];
    }
  }, [props.toolMessage]);

  const statusCounts = tasks.reduce(
    (acc, task) => {
      acc[task.canonical_status] = (acc[task.canonical_status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const baseBadge = (
    <BaseBadge
      {...allProps}
      showArgs={false}
      icon={ListTodo}
      resultTexts={{
        loading: 'Loading tasks...',
        done:
          tasks.length > 0
            ? `Found ${tasks.length} task${tasks.length !== 1 ? 's' : ''}`
            : 'No tasks found',
        error: 'Error loading tasks',
      }}
    />
  );

  if (tasks.length === 0) {
    return baseBadge;
  }

  return (
    <HoverCard openDelay={0} closeDelay={200}>
      <HoverCardTrigger asChild>
        <div>{baseBadge}</div>
      </HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-[500px] p-0">
        <div className="border-b border-border p-3 bg-muted/30">
          <div className="flex flex-wrap gap-2">
            {Object.entries(statusCounts).map(([status, count]) => (
              <div key={status} className="flex items-center gap-1">
                {getStatusIcon(status)}
                <span className="text-xs text-muted-foreground">
                  {count} {status}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="max-h-[400px] overflow-y-auto scrollbar">
          <TaskList tasks={tasks} />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
